import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  Home, DollarSign, BarChart3, FolderKanban, Settings,
  ChevronsLeft, ChevronsRight, Sun, Moon, LogOut, Menu, X,
  FileText, Users, Landmark, Store, Truck, CheckCircle,
  AlertCircle, Bell, Building2, Boxes, Wallet, RefreshCw
} from 'lucide-react';
import Notifications from '@/components/Notifications';

const DARK_STORAGE_KEY = 'siempre_theme_dark';
const COLLAPSE_STORAGE_KEY = 'siempre_sidebar_collapsed';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_b0e0e0df-2d06-4315-be1e-7d0387fd3a85/artifacts/eslg6eoj_imagen%282%29.png';

// Permission helper (mirror del backend)
const userHasPermission = (user, permission) => {
  if (!user) return false;
  if (user.role === 'admin') return true;
  const perms = user.permissions || [];
  return perms.includes(permission);
};

// Cada item puede especificar roles[] o permission (uno u otro).
const NAV_ITEMS = [
  { key: 'home', label: 'Inicio', icon: Home, path: '/dashboard' },
  { key: 'new-payment', label: 'Nuevo Pago', icon: DollarSign, path: '/payments/new' },
  { key: 'adelantos', label: 'Adelantos', icon: Wallet, path: '/adelantos', permission: 'ver_adelantos' },
  { key: 'flujo-sync', label: 'Flujo de Caja', icon: RefreshCw, path: '/flujo-sync', permission: 'gestionar_sync_flujo' },
  {
    key: 'reports',
    label: 'Reportes',
    icon: BarChart3,
    permission: 'ver_reportes',
    children: [
      { label: 'Reportes generales', path: '/reports', icon: FileText, permission: 'ver_reportes' },
      { label: 'Reporte Detallado', path: '/reporte-detallado', icon: BarChart3, permission: 'ver_reporte_detallado' },
      { label: 'Compras por Proveedor', path: '/reporte-compras', icon: BarChart3, permission: 'ver_reporte_compras_proveedor' },
      { label: 'Reporte ERP', path: '/macrobase-report', icon: CheckCircle, permission: 'ver_reporte_macrobase' },
      { label: 'Contado Vencidos', path: '/contado-vencidos', icon: AlertCircle, permission: 'ver_reporte_contado_vencido' },
      { label: 'Días de Visita', path: '/provider-visits', icon: Truck, permission: 'ver_reportes' },
    ],
  },
  {
    key: 'catalog',
    label: 'Catálogos',
    icon: FolderKanban,
    adminOnly: true,
    children: [
      { label: 'Proveedores', path: '/admin/providers', icon: Building2 },
      { label: 'Tiendas', path: '/admin/stores', icon: Store },
      { label: 'Bancos', path: '/admin/bancos', icon: Landmark },
      { label: 'Boletas', path: '/admin/boletas', icon: FileText },
    ],
  },
  {
    key: 'admin',
    label: 'Administración',
    icon: Settings,
    adminOnly: true,
    children: [
      { label: 'Usuarios', path: '/admin/users', icon: Users },
      { label: 'WhatsApp', path: '/admin/whatsapp', icon: Bell },
      { label: 'Configuración', path: '/admin/settings', icon: Settings },
      { label: 'Validación bancaria', path: '/admin/bank-validation', icon: CheckCircle },
    ],
  },
];

function useDarkMode() {
  const [dark, setDark] = useState(() => {
    try {
      return localStorage.getItem(DARK_STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });
  useEffect(() => {
    document.documentElement.classList.toggle('dark', dark);
    try {
      localStorage.setItem(DARK_STORAGE_KEY, String(dark));
    } catch { /* ignore */ }
  }, [dark]);
  return [dark, setDark];
}

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(COLLAPSE_STORAGE_KEY) === 'true'; } catch { return false; }
  });
  const [dark, setDark] = useDarkMode();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openGroups, setOpenGroups] = useState({ reports: true, catalog: false, admin: false });

  useEffect(() => {
    try { localStorage.setItem(COLLAPSE_STORAGE_KEY, String(collapsed)); } catch { /* ignore */ }
  }, [collapsed]);

  // Cerrar el drawer móvil al cambiar de ruta
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const canSeeItem = (item) => {
    if (item.adminOnly) return ['admin', 'gerencia', 'encargada_compras', 'asistente_admin'].includes(user?.role);
    if (item.permission) return userHasPermission(user, item.permission);
    return true;
  };

  const visibleNav = NAV_ITEMS.map((item) => {
    if (item.children) {
      const kids = item.children.filter(canSeeItem);
      if (kids.length === 0) return null;
      return { ...item, children: kids };
    }
    return canSeeItem(item) ? item : null;
  }).filter(Boolean);

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');
  const initials = (user?.name || 'U').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();

  const handleLogout = () => { logout(); navigate('/login'); };

  return (
    <div className={`min-h-screen bg-slate-50 dark:bg-slate-950 flex`} data-testid="app-shell">
      {/* Mobile overlay */}
      {mobileOpen && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setMobileOpen(false)} aria-hidden="true" />}

      {/* SIDEBAR */}
      <aside
        className={`
          fixed lg:sticky top-0 left-0 z-40 h-screen lg:h-screen flex-shrink-0
          ${collapsed ? 'lg:w-16' : 'lg:w-60'}
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0
          w-60 bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800
          flex flex-col transition-[width,transform] duration-200
        `}
        data-testid="app-sidebar"
      >
        <div className={`h-16 flex items-center ${collapsed ? 'lg:justify-center' : 'px-5 gap-3'} border-b border-slate-200 dark:border-slate-800 shrink-0`}>
          <img src={LOGO_URL} alt="SIEMPRE" className="h-9 w-9 object-contain" />
          {(!collapsed) && (
            <div className="min-w-0">
              <p className="font-display font-bold text-sm text-slate-900 dark:text-slate-100 leading-tight truncate">SIEMPRE</p>
              <p className="text-[10px] text-slate-500 dark:text-slate-500 uppercase tracking-widest">Portal Pagos</p>
            </div>
          )}
        </div>

        <nav className="flex-1 py-3 overflow-y-auto">
          {visibleNav.map((item) => {
            if (item.children) {
              const anyActive = item.children.some(c => isActive(c.path));
              const isOpen = collapsed ? false : (openGroups[item.key] ?? anyActive);
              return (
                <div key={item.key}>
                  <button
                    onClick={() => setOpenGroups(g => ({ ...g, [item.key]: !isOpen }))}
                    className={`w-full flex items-center h-10 pl-4 pr-3 gap-3 border-l-[3px] border-transparent
                      ${anyActive ? 'text-[#2c57c5]' : 'text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-slate-100'}
                    `}
                    data-testid={`nav-group-${item.key}`}
                    title={collapsed ? item.label : undefined}
                  >
                    <item.icon className="h-4 w-4 shrink-0" />
                    {!collapsed && <>
                      <span className="text-sm font-medium">{item.label}</span>
                      <ChevronsRight className={`h-3 w-3 ml-auto transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                    </>}
                  </button>
                  {isOpen && !collapsed && (
                    <div className="pl-4 pb-1">
                      {item.children.map(child => (
                        <button
                          key={child.path}
                          onClick={() => navigate(child.path)}
                          className={`w-full flex items-center gap-3 h-9 pl-6 pr-3 text-left transition-colors border-l-[3px] ${isActive(child.path) ? 'border-[#2c57c5] bg-blue-50 dark:bg-blue-950/40 text-[#2c57c5]' : 'border-transparent text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800/50 hover:text-slate-900 dark:hover:text-slate-100'}`}
                          data-testid={`nav-item-${child.path.replace(/\//g,'-')}`}
                        >
                          <child.icon className="h-3.5 w-3.5 shrink-0" />
                          <span className="text-[13px]">{child.label}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            }
            return (
              <button
                key={item.key}
                onClick={() => navigate(item.path)}
                className={`w-full flex items-center h-10 ${collapsed ? 'lg:justify-center lg:px-0 pl-4 pr-3' : 'pl-4 pr-3'} gap-3 border-l-[3px] transition-colors ${isActive(item.path) ? 'border-[#2c57c5] bg-blue-50 dark:bg-blue-950/40 text-[#2c57c5]' : 'border-transparent text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-slate-100'}`}
                data-testid={`nav-item-${item.path.replace(/\//g,'-')}`}
                title={collapsed ? item.label : undefined}
              >
                <item.icon className="h-4 w-4 shrink-0" />
                {!collapsed && <span className="text-sm font-medium">{item.label}</span>}
              </button>
            );
          })}
        </nav>

        <button
          onClick={() => setCollapsed(!collapsed)}
          className="hidden lg:flex mx-3 mb-2 h-9 items-center justify-center rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 dark:text-slate-400 transition-colors"
          data-testid="sidebar-toggle"
          aria-label={collapsed ? 'Expandir menú' : 'Colapsar menú'}
        >
          {collapsed ? <ChevronsRight className="h-4 w-4"/> : <ChevronsLeft className="h-4 w-4"/>}
        </button>

        {/* Botón "Salir" siempre visible dentro del sidebar */}
        <button
          onClick={handleLogout}
          className={`mx-3 mb-4 h-10 flex items-center ${collapsed ? 'lg:justify-center lg:px-0' : 'px-3'} gap-2 rounded-md text-sm font-medium text-slate-600 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors border border-slate-200 dark:border-slate-800`}
          data-testid="sidebar-logout"
          title="Cerrar sesión"
          aria-label="Cerrar sesión"
        >
          <LogOut className="h-4 w-4 shrink-0" />
          {!collapsed && <span>Salir</span>}
        </button>
      </aside>

      {/* MAIN COLUMN */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* HEADER */}
        <header className="h-16 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 sticky top-0 z-20 flex items-center justify-between px-4 lg:px-6">
          <button
            className="lg:hidden h-9 w-9 flex items-center justify-center rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-400"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Menú"
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X className="h-5 w-5"/> : <Menu className="h-5 w-5"/>}
          </button>
          <div className="hidden lg:block">
            <p className="text-xs text-slate-500 dark:text-slate-500 font-medium">Bienvenido</p>
            <p className="text-sm text-slate-900 dark:text-slate-100 font-semibold" data-testid="header-username">{user?.name}</p>
          </div>
          <div className="flex items-center gap-2 ml-auto">
            <Notifications />
            <button
              onClick={() => setDark(!dark)}
              className="h-9 w-9 flex items-center justify-center rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-400 transition-colors"
              aria-label={dark ? 'Modo claro' : 'Modo oscuro'}
              data-testid="theme-toggle"
              title={dark ? 'Modo claro' : 'Modo oscuro'}
            >
              {dark ? <Sun className="h-4 w-4"/> : <Moon className="h-4 w-4"/>}
            </button>
            <div className="flex items-center gap-2 pl-3 border-l border-slate-200 dark:border-slate-800">
              <div className="h-8 w-8 rounded-full bg-gradient-to-br from-[#2c57c5] to-[#1e3a8a] flex items-center justify-center text-white text-xs font-semibold" data-testid="header-avatar">{initials}</div>
              <button
                onClick={handleLogout}
                className="inline-flex h-8 items-center gap-1.5 px-2 sm:px-3 text-xs font-medium text-slate-600 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                data-testid="header-logout"
                aria-label="Salir"
                title="Salir"
              >
                <LogOut className="h-4 w-4"/>
                <span className="hidden sm:inline">Salir</span>
              </button>
            </div>
          </div>
        </header>

        {/* CONTENT */}
        <main className="flex-1 overflow-y-auto">
          {children}
        </main>
      </div>
    </div>
  );
}
